import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Stack, Text } from 'shell/components';
import { useViewport } from 'contexts/viewport';

const MilestoneStatus = ({ milestones = [] }) => {
    const { device } = useViewport();
    const theme = useTheme();
    const [milestoneStatus, setMilestoneStatus] = useState({});

    const tabletMobileStatus = () => {
        return (
            <Stack
                sx={{ width: '100%' }}
                direction={device === 'desktop' ? 'row' : 'column'}
                spacing={device === 'desktop' ? 5 : 1}
            >
                <StatusTextStack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomDotBox color={theme.palette.ensightful.error[300]} />
                        <Text variant={'p3'} color={'light'}>
                            Overdue
                        </Text>
                    </Stack>
                    <Text weight={'semibold'}>{milestoneStatus?.overdue?.count}</Text>
                </StatusTextStack>
                <StatusTextStack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomDotBox color={theme.palette.ensightful.primary[300]} />
                        <Text variant={'p3'} color={'light'}>
                            Upcoming
                        </Text>
                    </Stack>
                    <Text variant={'p2'} weight={'semibold'}>
                        {milestoneStatus?.upcoming?.count}
                    </Text>
                </StatusTextStack>
                <StatusTextStack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomDotBox color={theme.palette.ensightful.success[200]} />
                        <Text variant={'p3'} color={'light'}>
                            Completed
                        </Text>
                    </Stack>
                    <Text variant={'p2'} weight={'semibold'}>
                        {milestoneStatus?.completed?.count}
                    </Text>
                </StatusTextStack>
            </Stack>
        );
    };

    const desktopStatus = () => {
        return (
            <Stack direction={'row'} spacing={5}>
                <StatusTextStack alignItems={'center'} direction={'row'} spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomDotBox color={theme.palette.ensightful.error[300]} />
                        <Text variant={'p3'} color={'light'}>
                            Overdue
                        </Text>
                    </Stack>
                    <Text weight={'semibold'}>{milestoneStatus?.overdue?.count}</Text>
                </StatusTextStack>
                <StatusTextStack alignItems={'center'} direction={'row'} spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomDotBox color={theme.palette.ensightful.primary[300]} />
                        <Text variant={'p3'} color={'light'}>
                            Upcoming
                        </Text>
                    </Stack>
                    <Text variant={'p2'} weight={'semibold'}>
                        {milestoneStatus?.upcoming?.count}
                    </Text>
                </StatusTextStack>
                <StatusTextStack alignItems={'center'} direction={'row'} spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomDotBox color={theme.palette.ensightful.success[200]} />
                        <Text variant={'p3'} color={'light'}>
                            Completed
                        </Text>
                    </Stack>
                    <Text variant={'p2'} weight={'semibold'}>
                        {milestoneStatus?.completed?.count}
                    </Text>
                </StatusTextStack>
            </Stack>
        );
    };

    useEffect(() => {
        const overdueLength = milestones?.filter(
            (milestone) => milestoneStatus !== 'completed' && milestone.end_on < new Date().getTime()
        ).length;
        const upcomingLength = milestones?.filter(
            (milestone) => milestoneStatus !== 'completed' && milestone.end_on >= new Date().getTime()
        ).length;
        const completedLength = milestones?.filter((milestone) => milestone.status === 'completed').length;
        const status = {
            overdue: {
                count: overdueLength,
                percentage: Math.round((overdueLength / milestones.length) * 100),
            },
            upcoming: {
                count: upcomingLength,
                percentage: Math.round((upcomingLength / milestones.length) * 100),
            },
            completed: {
                count: completedLength,
                percentage: Math.round((completedLength / milestones.length) * 100),
            },
        };
        setMilestoneStatus(status);
    }, [milestones]);

    return (
        <CustomStack spacing={6}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p1'} weight={'semibold'}>
                    Milestones
                </Text>
                <Text variant={'p3'}>
                    Total{' '}
                    <Text component={'span'} variant={'p2'} weight={'semibold'}>
                        {milestones.length}
                    </Text>
                </Text>
            </Stack>
            <BarContainerStack direction={'row'} spacing={0}>
                <CustomBarStack
                    color={theme.palette.ensightful.error[300]}
                    width={`${milestoneStatus?.overdue?.percentage || 0}%`}
                />
                <CustomBarStack
                    color={theme.palette.ensightful.primary[300]}
                    width={`${milestoneStatus?.upcoming?.percentage || 0}%`}
                />
                <CustomBarStack
                    color={theme.palette.ensightful.success[200]}
                    width={`${milestoneStatus?.completed?.percentage || 0}%`}
                />
            </BarContainerStack>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-start'} spacing={5}>
                {milestones.length === 0 ? (
                    <Text color={'light'}>No milestones have been created</Text>
                ) : device === 'desktop' ? (
                    desktopStatus()
                ) : (
                    tabletMobileStatus()
                )}
            </Stack>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(6),
        borderRadius: theme.spacing(4),
        boxShadow: theme.palette.ensightful.shadows[1],
        background: theme.palette.ensightful.shades[0],
    };
});

const BarContainerStack = styled(Stack)(({ theme }) => {
    return {
        overflow: 'hidden',
        borderRadius: '4px',
        width: '100%',
        height: '20px',
        background: theme.palette.ensightful.shades[0],
    };
});

const CustomBarStack = styled(Stack)(({ width, color, borderRadius }) => {
    return {
        borderRadius: borderRadius,
        width: width,
        height: '20px',
        background: color,
    };
});

const CustomDotBox = styled(Stack)(({ color }) => {
    return {
        borderRadius: '50%',
        width: '8px',
        height: '8px',
        background: color,
    };
});

const StatusTextStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

export default MilestoneStatus;
